import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { isArray } from 'util';
import { omit } from 'lodash';
import {
  FilterName,
  FiltersDatepickerRangeOutput,
  FiltersService,
  ListFilter,
} from '../../../../store/models/filter.model';
import {
  MonitoringCollectionBasedFilter,
  MonitoringCountryFilters,
  MonitoringDateRangeFilter,
  MonitoringLabelBasedFilter,
  MonitoringValueBasedTimeRangeFilter,
} from '../../../../store/state/monitoring/models/monitoring-filters.model';
import { AllocationViewListRangeDate } from '../../../../store/state/monitoring/allocation-view/constants/allocation-view-filters.constant';
import { DATE_TIME_PICKER_DISPLAY_DATE_HOURS_FIRST_FORMAT } from '../../../../common/constants/date-config';
import moment from 'moment';
import { BooleanData, UserListFilterName } from '../../../../store/state/administration';
import { TRANSLATED_FILTER_TITLES } from '../../../../store/constants';
import { IconNames } from '../../../../common/constants';
import { AutoUnsubscribe } from '../../../../common/decorators';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'ui-monitoring-list-chips-toolbar',
  templateUrl: './monitoring-list-chips-toolbar.component.html',
  styleUrls: ['./monitoring-list-chips-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class MonitoringListChipsToolbarComponent implements OnInit {
  @Input() set filtersValue(data: Partial<FiltersDatepickerRangeOutput>) {
    if (data) {
      this.data$.next(data);
    }
  }

  @Input() listFiltersService: FiltersService;

  filterList: ListFilter[];
  translatedFilterTitles: Map<string, string> = TRANSLATED_FILTER_TITLES;
  approved: string = BooleanData.True;
  iconNameList = IconNames;
  showAllFilters = true;
  showHideFilterLevelLimit = 5;
  private data$ = new BehaviorSubject<Partial<FiltersDatepickerRangeOutput> | null>(null);
  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    this.subscriptions.push(this.onDataSubscription());
  }

  isDateRangeFilter(filterTitle: string): boolean {
    return Object.values(MonitoringDateRangeFilter).includes(filterTitle as MonitoringDateRangeFilter);
  }

  isLabelBasedFilter(groupTitle: string): boolean {
    return Object.values(MonitoringLabelBasedFilter).includes(groupTitle as MonitoringLabelBasedFilter);
  }

  isCollectionBasedFilter(groupTitle: string): boolean {
    return Object.values(MonitoringCollectionBasedFilter).includes(groupTitle as MonitoringCollectionBasedFilter);
  }

  isRoleBasedFilter(groupTitle: string): boolean {
    return groupTitle === UserListFilterName.AssignedRoles;
  }

  isCountryBasedFilter(filterTitle: string): boolean {
    return Object.values(MonitoringCountryFilters).includes(filterTitle as MonitoringCountryFilters);
  }

  toggleFiltersVisibility(): void {
    this.showAllFilters = !this.showAllFilters;
  }

  private onDataSubscription(): Subscription {
    return this.data$
      .pipe(filter(Boolean))
      .subscribe((data: Partial<FiltersDatepickerRangeOutput>) => this.initFilterList(data));
  }

  private initFilterList(data: Partial<FiltersDatepickerRangeOutput>): void {
    data = omit(data, ['viewMode', 'statisticFilter']);

    this.filterList = Object.entries(data)
      // TODO: refactor #14575
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      .map<ListFilter>(([key, values]: [FilterName, any]) => {
        switch (true) {
          case this.isTimeFrame(key):
            if (values) {
              const { start, end } = values;
              values = [`${this.convertDate(start)} - ${this.convertDate(end)}`];
            }
            break;
          case this.isDateRangeFilter(key):
            values =
              values?.start || values?.end || values?.daysFromToday || values?.daysFromToday === 0 ? [key] : null;
            break;
          case !isArray(values):
            values = values ? [values] : null;
            break;
        }

        return {
          title: key,
          values,
        };
      })
      .filter((item: ListFilter) => item.values && item.values.length);

    this.listFiltersService?.activeFilterNameList$.next(
      this.filterList.map((filterItem: ListFilter) => filterItem.title),
    );
  }

  private isTimeFrame(key: string): boolean {
    return Object.values(MonitoringValueBasedTimeRangeFilter).includes(key as AllocationViewListRangeDate);
  }

  private convertDate(date: number): string {
    return moment(date).format(DATE_TIME_PICKER_DISPLAY_DATE_HOURS_FIRST_FORMAT);
  }
}
